import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Logo from "@/components/Logo";
import { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      const to = location.state?.from?.pathname || "/dashboard";
      toast.success("Bem-vindo de volta!");
      navigate(to, { replace: true });
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Visual side — hero image (risco/logística/aprendizado) com overlay para contraste */}
      <div
        className="hidden lg:flex flex-1 relative text-white p-12 flex-col justify-between overflow-hidden"
        style={{
          backgroundImage:
            "linear-gradient(135deg, rgba(11,20,44,0.92) 0%, rgba(11,20,44,0.72) 55%, rgba(197,34,44,0.55) 100%), url('https://images.unsplash.com/photo-1573164713988-8665fc963095?auto=format&fit=crop&w=1600&q=80')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="inline-flex bg-white/95 rounded-lg px-4 py-3 shadow-md w-fit">
          <Logo size={44} />
        </div>
        <div className="max-w-md relative">
          <div className="eyebrow text-white/80 mb-3">Plataforma UNIGR</div>
          <h2 className="font-display text-4xl font-extrabold leading-tight drop-shadow-lg">
            Capacite, certifique e audite sua operação.
          </h2>
          <p className="mt-4 text-white/85 drop-shadow">
            Treinamento operacional pensado para gestão de riscos, logística e monitoramento 24/7 — do jovem aprendiz ao supervisor de central.
          </p>
          <div className="mt-6 flex flex-wrap gap-2">
            <span className="text-xs px-3 py-1 rounded-full bg-white/10 backdrop-blur border border-white/20">Gestão de Riscos</span>
            <span className="text-xs px-3 py-1 rounded-full bg-white/10 backdrop-blur border border-white/20">Logística</span>
            <span className="text-xs px-3 py-1 rounded-full bg-white/10 backdrop-blur border border-white/20">Aprendizado Contínuo</span>
          </div>
        </div>
        <div className="text-xs text-white/60 relative">© 2026 UNIGR · conhecimento que transforma</div>
      </div>

      {/* Form side */}
      <div className="flex-1 flex items-center justify-center p-6 md:p-12 bg-white">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8"><Logo /></div>
          <div className="eyebrow mb-2">Acesso</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Entre na plataforma</h1>
          <p className="text-sm text-slate-600 mt-2">Use seu e-mail corporativo para acessar.</p>

          <form onSubmit={onSubmit} className="mt-8 space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email">E-mail</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="voce@empresa.com.br"
                required
                data-testid="login-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password">Senha</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password-input"
              />
            </div>
            {error && <div className="text-sm text-red-600" data-testid="login-error">{error}</div>}
            <Button type="submit" disabled={loading} className="btn-navy w-full" data-testid="login-submit-btn">
              {loading ? "Entrando…" : "Entrar"}
            </Button>
          </form>

          <div className="mt-6 text-sm text-slate-600">
            Ainda não tem acesso? <Link to="/register" className="text-[var(--unigr-navy)] font-medium hover:underline">Solicitar conta</Link>
          </div>
          <div className="mt-3 text-sm">
            <Link to="/" className="text-slate-500 hover:underline">← Voltar para o site</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
